/**
 * Unified Product Data Architecture
 *
 * This module defines the core data structures for the gear catalog,
 * supporting both detailed thermometer specifications and general e-commerce products.
 *
 * @module @backyahd/shared/types/product
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export enum ProductCategory {
  ACCESSORIES = 'Accessories',
  BOOKS = 'Books',
  BREAD = 'Bread',
  BUTCHER_PAPER = 'Butcher Paper',
  COFFEE = 'Coffee',
  FUEL = 'Fuel',
  FREEZERS = 'Freezers',
  GRIDDLES = 'Griddles',
  GRILLS = 'Grills',
  GRILL_GAUGES = 'Grill Gauges',
  INSTANT_READ_THERMOMETERS = 'Instant-Read Thermometers',
  KNIVES = 'Knives',
  MEAT = 'Meat',
  OVENS = 'Ovens',
  SEASONINGS = 'Seasonings',
  SMOKERS = 'Smokers',
  TOOLS = 'Tools',
  WIRELESS_THERMOMETERS = 'Wireless Thermometers',
}

export enum ProductDataSource {
  PRODUCTS_CACHE = 'productsCache',
}

// ============================================================================
// PRICE STRUCTURES
// ============================================================================

/**
 * Tiered pricing structure for products with quantity-based pricing
 */
export interface PriceStructure {
  qty1?: number;
  qty2?: number;
  qty3?: number;
  qty4?: number;
  notes?: string;
}

/**
 * Union type for all valid price values
 */
export type PriceValue = number | PriceStructure | null;

// ============================================================================
// PURCHASE LINK INTERFACE
// ============================================================================

/**
 * Represents a purchase destination for a product (Amazon, manufacturer site, etc.)
 */
export interface PurchaseLink {
  name: string;
  buttonText: string;
  url: string;
  linkType: 'amazon' | 'manufacturer' | 'retailer' | 'affiliate';
  sortOrder: number;
}

// ============================================================================
// CORE PRODUCT INTERFACES
// ============================================================================

/**
 * Base product interface that all products must implement
 * Contains common fields found in both data sources
 */
export interface BaseProduct {
  // Core identification
  id: string;
  name: string;
  brand: string;
  brandId?: number;
  /** Public marketing copy only; the actual code remains server-gated. */
  discountDescription?: string;
  model?: string;
  externalId?: string; // Amazon ASIN or other external product ID

  // Pricing
  price?: number; // Primary price point
  salePrice?: number;

  // Basic metadata
  category: ProductCategory;
  subcategory?: string;
  rating?: number;

  // Media & Links
  image?: string;
  imageUrl?: string; // Alternative image field
  logo?: string;
  url?: string;

  // Basic product info
  title?: string;
  description?: string;
  sourceSite?: string;
  recommended?: boolean;
  show?: boolean;
  featured?: boolean;

  // Rating and reviews
  reviewCount?: number;

  // Pricing extensions
  originalPrice?: number; // For sale prices

  // Data source tracking
  dataSource: ProductDataSource;
  originalId: string; // ID from source data

  // Common optional fields
  notes?: string;
  pros?: string[];
  cons?: string[];
  awards?: string[];

  // Post relationship tracking
  postCount?: number; // Number of posts this product appears in

  // Purchase destinations
  purchaseLinks?: PurchaseLink[];
}

/**
 * Extended product interface for products requiring detailed comparison
 * Currently used for wireless thermometers, designed to be extensible
 */
export interface DetailedProduct extends BaseProduct {
  // Enhanced pricing structure
  priceStructure?: {
    notes?: string;
    qty1?: number;
    qty2?: number;
    qty3?: number;
    qty4?: number;
  };

  // Shipping & commerce
  shipping?: string;
  freeShipping?: boolean;

  // Physical specifications (thermometer-specific, but extensible)
  physicalSpecs?: {
    overallLength?: UnitMeasurement;
    probeLength?: UnitMeasurement;
    diameter?: UnitMeasurement;
    minimumInsertionDepth?: UnitMeasurement;
    material?: string;
  };

  // Technical specifications (category-specific)
  technicalSpecs?: {
    // Thermometer-specific
    sensors?: SensorConfiguration;
    numberOfTempReadingsInApp?: number;
    tempAccuracy?: TemperatureAccuracy;
    tempDataLoggingInterval?: number;

    // Connectivity (applicable to various smart products)
    wifi?: boolean;
    bluetoothVersion?: number;
    subGhzFrequency?: number;
    unobstructedRange?: UnitMeasurement;
    obstructedRange?: UnitMeasurement;
  };

  // Feature specifications (category-specific)
  features?: {
    // Thermometer-specific
    cookHistoryWeb?: boolean;
    cookHistoryApp?: boolean;
    hubDisplay?: boolean;
    magneticHub?: boolean;
    magneticProbeCase?: boolean;
    highLowTempAlerts?: boolean;

    // Temperature ranges (thermometer-specific)
    internalTempRange?: TemperatureRange;
    ambientTempRange?: TemperatureRange;

    // Power & battery (applicable to various products)
    probeBatteryLife?: BatteryLife;
    probeRechargeTime?: number;
    hubBatteryLife?: number;
    hubRechargeTime?: number;

    // Safety & certification (applicable to various products)
    sousVideSafe?: boolean;
    dishwasherSafe?: boolean;
    deepFryerSafe?: boolean;
    ovenSafe?: boolean;
    nsfApproved?: boolean;
    calibrationCertificate?: boolean;
    waterResistance?: string;
    certifications?: string[];
  };

  // Support & warranty
  warranty?: number; // years
  returnPolicy?: number; // days
  support?: string;
  manufacturingLocation?: string;
  designLocation?: string;

  // Enhanced content
  hubDisplayNotes?: string;
  recommendation?: string;
}

/**
 * Interface for products from products_cache.json
 * Represents the e-commerce structure
 */
export interface ProductCacheItem {
  Id: number;
  ExternalId: string | null;
  BrandId: number;
  ProductCategoryId: number | null;
  Name: string;
  Title: string;
  Url: string;
  SmallImageUrl?: string | null;
  MediumImageUrl?: string | null;
  Price: string; // String format in source
  Site: string | null;
  Brand?: string | null;
  Show: boolean;
  IsFeatured?: boolean;
  SalePrice?: string | null;
  BrandName: string;
  MediumLogoUrl?: string | null;
  CategoryName: string | null;
  CreatorBrandCode?: string | null;
  CreatorBrandDiscount?: string | null;
  /** Public marketing copy only; never contains the discount code. */
  DiscountDescription?: string | null;
  Sort: number;
  PostProductCount: number;
  purchaseLinks?: PurchaseLink[];
}

// ============================================================================
// SUPPORTING INTERFACES
// ============================================================================

export interface UnitMeasurement {
  imperial?: number | null;
  metric?: number | null;
  imperialMeasurement?: string | null;
  metricMeasurement?: string | null;
  notes?: string | null;
}

export interface TemperatureAccuracy {
  imperial?: number | null;
  metric?: number | null;
  notes?: string | null;
}

export interface SensorConfiguration {
  internal?: number | null;
  ambient?: number | null;
  notes?: string | null;
}

export interface TemperatureRange {
  min?: {
    imperial?: number | null;
    metric?: number | null;
  } | null;
  max?: {
    imperial?: number | null;
    metric?: number | null;
    mins?: number | null;
  } | null;
  notes?: string | null;
}

export interface BatteryLife {
  min?: number;
  max?: number;
  duration?: {
    minutes?: number;
    hours?: number;
  };
  notes?: string | null;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Union type representing any product in the system
 */
export type Product = BaseProduct | DetailedProduct;

/**
 * Type guard to check if a product is a detailed product
 */
export function isDetailedProduct(product: Product): product is DetailedProduct {
  return 'physicalSpecs' in product || 'technicalSpecs' in product || 'features' in product;
}

/**
 * Type guard to check if a product supports comparison
 */
export function supportsComparison(product: Product): boolean {
  return product.category === ProductCategory.WIRELESS_THERMOMETERS;
}

/**
 * Allowed primitive values in raw thermometer data
 */
export type RawDataValue = string | number | boolean | null | undefined;

/**
 * Raw data interfaces for transformation utilities
 */
export interface RawThermometerData {
  id: string;
  brand?: string;
  model?: string;
  rating?: number;
  price?: PriceValue;
  shipping?: string;
  freeShipping?: boolean;
  link?: string;
  image?: string;
  logo?: string;
  // ... all other thermometer fields
  [key: string]: RawDataValue | RawDataValue[] | PriceStructure | Record<string, RawDataValue>;
}

export interface RawProductCacheData {
  timestamp: string;
  products: ProductCacheItem[];
}

// ============================================================================
// VALIDATION SCHEMAS
// ============================================================================

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationWarning {
  field: string;
  message: string;
  suggestion?: string;
}

// ============================================================================
// TRANSFORMATION UTILITIES
// ============================================================================

/**
 * Configuration for data transformation
 */
export interface TransformationConfig {
  // Field mapping configurations
  fieldMappings: Record<string, string>;

  // Validation rules
  requiredFields: string[];
  optionalFields: string[];

  // Data cleaning rules
  priceNormalization: boolean;
  textCleaning: boolean;
  urlValidation: boolean;
}

/**
 * Result of data transformation
 */
export interface TransformationResult<T> {
  success: boolean;
  data?: T;
  errors: TransformationError[];
  warnings: TransformationWarning[];
  sourceData: Record<string, unknown>;
}

export interface TransformationError {
  field: string;
  message: string;
  value?: unknown;
}

export interface TransformationWarning {
  field: string;
  message: string;
  originalValue?: unknown;
  transformedValue?: unknown;
}
